/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, memo } from 'react';

import { css, jsx } from '@compiled/react';

import EditorDoneIcon from '@atlaskit/icon/core/check-mark';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';
import type { IconColor } from '@atlaskit/tokens/css-type-schema';
import Tooltip from '@atlaskit/tooltip';

import type { ColorProps } from './types';

const buttonWrapperStyles = css({
	borderColor: 'transparent',
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	borderRadius: token('radius.small', '4px'),
	'&:focus-within, &:focus, &:hover': {
		borderColor: token('color.border'),
	},
});

const buttonWrapperStylesNew = css({
	borderColor: 'transparent',
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	borderRadius: token('radius.medium', '6px'),
	'&:focus-within, &:focus, &:hover': {
		borderColor: token('color.border'),
	},
});

const buttonStyles = css({
	height: token('space.300'),
	width: token('space.300'),
	backgroundColor: token('color.background.neutral'),
	padding: 0,
	borderRadius: token('radius.small', '4px'),
	border: `${token('border.width')} solid ${token('color.border.inverse')}`,
	cursor: 'pointer',
	display: 'block',
	position: 'relative',
	'&:focus': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});

/**
 * Individual color palette item component
 * Displays a single color swatch with tooltip and selection state
 */
export const Color: React.NamedExoticComponent<ColorProps> = memo<ColorProps>(
	({
		autoFocus,
		tabIndex,
		value,
		label,
		isSelected,
		borderColor,
		checkMarkColor = token('color.icon.inverse'),
		hexToPaletteColor,
		decorator,
		onClick,
		onKeyDown,
	}) => {
		const colorStyle = hexToPaletteColor ? hexToPaletteColor(value) : value;

		const handleMouseDown = useCallback((e: React.MouseEvent) => {
			e.preventDefault();
		}, []);

		const handleClick = useCallback(
			(e: React.MouseEvent) => {
				e.preventDefault();
				onClick(value, label, e);
			},
			[onClick, value, label],
		);

		const handleKeyDown = useCallback(
			(e: React.KeyboardEvent) => {
				if (!onKeyDown) {
					return;
				}
				e.preventDefault();
				onKeyDown(value, label, e);
			},
			[onKeyDown, value, label],
		);

		if (
			expValEqualsNoExposure('platform_editor_lovability_text_bg_color', 'isEnabled', true) &&
			fg('platform_editor_lovability_text_bg_color_patch_1')
		) {
			return (
				<Tooltip content={label}>
					<span css={buttonWrapperStylesNew}>
						<button
							type="button"
							css={buttonStyles}
							aria-label={label}
							role="radio"
							aria-checked={isSelected}
							onClick={handleClick}
							onKeyDown={handleKeyDown}
							onMouseDown={handleMouseDown}
							tabIndex={tabIndex}
							style={{
								backgroundColor: colorStyle || token('color.background.input'),
								border: `${token('border.width')} solid ${borderColor}`,
							}}
							autoFocus={autoFocus}
						>
							{!decorator && isSelected && (
								<EditorDoneIcon color={checkMarkColor as IconColor} label="" />
							)}
							{decorator}
						</button>
					</span>
				</Tooltip>
			);
		}

		return (
			<Tooltip content={label}>
				<span css={buttonWrapperStyles}>
					<button
						type="button"
						css={buttonStyles}
						aria-label={label}
						role="radio"
						aria-checked={isSelected}
						onClick={handleClick}
						onKeyDown={handleKeyDown}
						onMouseDown={handleMouseDown}
						tabIndex={tabIndex}
						style={{
							backgroundColor: colorStyle || token('color.background.input'),
							border: `${token('border.width')} solid ${borderColor}`,
						}}
						autoFocus={autoFocus}
					>
						{!decorator && isSelected && (
							<EditorDoneIcon color={checkMarkColor as IconColor} label="" />
						)}
						{decorator}
					</button>
				</span>
			</Tooltip>
		);
	},
);
